import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../../utils/toast";
import {
  Check,
  ChevronDown,
  Copy,
  KeyRound,
  Plus,
  RefreshCw,
  Trash2,
  Webhook,
} from "lucide-react";

import { Badge } from "../../../../components/ui/Badge";
import { Button } from "../../../../components/ui/Button";
import { FieldLabel } from "../../../../components/ui/FieldLabel";
import { Input } from "../../../../components/ui/Input";
import {
  createWebhookSubscription,
  deleteWebhookSubscription,
  getWebhookDeliveries,
  getWebhookSubscriptions,
  rotateWebhookSecret,
} from "../../../../api/webhooks";

const DOC_URL = "https://beartrackapp.com/desarrolladores/webhooks";

const TONO_ENTREGA = {
  sent: "success",
  pending: "info",
  sending: "warning",
  dead: "error",
};

const ETIQUETA_ENTREGA = {
  sent: "Entregada",
  pending: "En cola",
  sending: "Enviando",
  dead: "Descartada",
};

const fecha = (v) =>
  v ? new Date(v).toLocaleString("es-CL", { dateStyle: "short", timeStyle: "short" }) : "—";

/**
 * Webhooks del operador.
 *
 * A diferencia de la pantalla de superadmin, acá NO hay selector de alcance: el
 * backend lo impone desde el JWT y un operador sólo puede administrar los suyos.
 * Mandar un `scope` distinto no cambiaría nada, así que la UI no lo ofrece.
 *
 * El secreto se muestra UNA vez. Es el detalle que más soporte genera si no
 * queda claro, así que va en un bloque destacado y con botón de copiar.
 */
export default function WebhooksSection() {
  const { t } = useTranslation();

  const [subs, setSubs] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [creando, setCreando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({ name: "", url: "" });
  const [secreto, setSecreto] = useState(null);
  const [copiado, setCopiado] = useState(false);
  const [entregasDe, setEntregasDe] = useState(null);
  const [entregas, setEntregas] = useState([]);

  const cargar = useCallback(async () => {
    setCargando(true);
    const r = await getWebhookSubscriptions();
    if (r?.error) {
      toast.error(String(r.error));
      setSubs([]);
    } else {
      setSubs(r);
    }
    setCargando(false);
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  async function crear(e) {
    e.preventDefault();
    setGuardando(true);
    const r = await createWebhookSubscription({
      name: form.name.trim(),
      url: form.url.trim(),
    });
    setGuardando(false);

    if (r?.error) {
      toast.error(String(r.error));
      return;
    }
    // Se muestra ANTES de recargar: si la recarga falla, el secreto no puede
    // perderse por eso.
    if (r?.secret) setSecreto(r.secret);
    setForm({ name: "", url: "" });
    setCreando(false);
    cargar();
  }

  async function rotar(sub) {
    const r = await rotateWebhookSecret(sub.id);
    if (r?.error) {
      toast.error(String(r.error));
      return;
    }
    if (r?.secret) setSecreto(r.secret);
    cargar();
  }

  async function eliminar(sub) {
    const r = await deleteWebhookSubscription(sub.id);
    if (r?.error) {
      toast.error(String(r.error));
      return;
    }
    toast.success(t("Webhook desactivado"));
    cargar();
  }

  async function verEntregas(sub) {
    if (entregasDe === sub.id) {
      setEntregasDe(null);
      return;
    }
    setEntregasDe(sub.id);
    setEntregas([]);
    const r = await getWebhookDeliveries(sub.id, { limit: 20 });
    setEntregas(r?.error ? [] : r);
  }

  async function copiar(valor) {
    try {
      await navigator.clipboard.writeText(valor);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1800);
    } catch {
      // Portapapeles bloqueado: el valor sigue visible y seleccionable.
    }
  }

  return (
    <section className="flex flex-col gap-5">
      <header>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-fg">
          <Webhook className="h-5 w-5 text-fg-muted" />
          {t("Webhooks")}
        </h2>
        <p className="mt-1 text-sm text-fg-muted">
          {t(
            "Recibe en tu sistema cada cambio de estado de tus envíos, sin tener que consultarnos. Te hacemos un POST firmado a la URL que registres.",
          )}{" "}
          <a
            href={DOC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-600 underline underline-offset-2"
          >
            {t("Ver la documentación técnica")}
          </a>
        </p>
      </header>

      {secreto && (
        <div className="rounded-lg border border-status-warning/40 bg-status-warning-soft/40 p-4">
          <p className="flex items-center gap-2 text-sm font-medium text-fg">
            <KeyRound className="h-4 w-4" />
            {t("Guarda este secreto ahora: no se vuelve a mostrar.")}
          </p>
          <p className="mt-1 text-xs text-fg-muted">
            {t("Con él tu servidor verifica que el envío viene de Beartrack.")}
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <code className="min-w-0 flex-1 break-all rounded bg-bg-muted px-2 py-1 text-xs">
              {secreto}
            </code>
            <Button variant="secondary" size="sm" onClick={() => copiar(secreto)}>
              {copiado ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copiado ? t("Copiado") : t("Copiar")}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setSecreto(null)}>
              {t("Listo")}
            </Button>
          </div>
        </div>
      )}

      {cargando ? (
        <p className="text-sm text-fg-muted">{t("Cargando…")}</p>
      ) : subs.length === 0 && !creando ? (
        <div className="rounded-lg border border-dashed border-border p-6 text-center">
          <p className="text-sm text-fg-muted">
            {t("Todavía no tienes ningún webhook configurado.")}
          </p>
          <Button className="mt-3" onClick={() => setCreando(true)}>
            <Plus className="h-4 w-4" />
            {t("Crear el primero")}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {subs.map((s) => (
            <div key={s.id} className="rounded-lg border border-border p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-fg">{s.name}</p>
                    <Badge tone={s.active ? "success" : "neutral"}>
                      {s.active ? t("Activo") : t("Inactivo")}
                    </Badge>
                  </div>
                  <p className="mt-0.5 break-all text-xs text-fg-muted">{s.url}</p>
                  {s.secretHint && (
                    <p className="mt-1 text-xs text-fg-muted">
                      {t("Secreto")}: <code>…{s.secretHint}</code>
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    title={t("Ver entregas")}
                    onClick={() => verEntregas(s)}
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        entregasDe === s.id ? "rotate-180" : ""
                      }`}
                    />
                    {t("Entregas")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title={t("Generar un secreto nuevo")}
                    onClick={() => rotar(s)}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    title={t("Desactivar")}
                    onClick={() => eliminar(s)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {entregasDe === s.id && (
                <div className="mt-4 border-t border-border pt-3">
                  {entregas.length === 0 ? (
                    <p className="text-xs text-fg-muted">
                      {t(
                        "Sin entregas todavía. Aparecen aquí en cuanto uno de tus envíos cambie de estado.",
                      )}
                    </p>
                  ) : (
                    <ul className="flex flex-col gap-2">
                      {entregas.map((e) => (
                        <li
                          key={e.id}
                          className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs"
                        >
                          <Badge tone={TONO_ENTREGA[e.deliveryStatus] ?? "neutral"}>
                            {t(ETIQUETA_ENTREGA[e.deliveryStatus] ?? e.deliveryStatus)}
                          </Badge>
                          <span className="text-fg-muted">{fecha(e.createdAt)}</span>
                          {e.lastHttpStatus && (
                            <code className="text-fg-muted">HTTP {e.lastHttpStatus}</code>
                          )}
                          {e.lastError && (
                            <span
                              className="max-w-[320px] truncate text-status-error"
                              title={e.lastError}
                            >
                              {e.lastError}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}

          {!creando && (
            <div>
              <Button variant="secondary" onClick={() => setCreando(true)}>
                <Plus className="h-4 w-4" />
                {t("Agregar otro")}
              </Button>
            </div>
          )}
        </div>
      )}

      {creando && (
        <form
          className="flex flex-col gap-3 rounded-lg border border-border p-4"
          onSubmit={crear}
        >
          {/* El Input de este panel es un input pelado: la etiqueta y la ayuda
              van aparte, con FieldLabel, que es el patrón del resto de settings. */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="wh-nombre">{t("Nombre")}</FieldLabel>
            <Input
              id="wh-nombre"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder={t("Mi ERP")}
              required
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="wh-url">{t("URL de destino")}</FieldLabel>
            <Input
              id="wh-url"
              type="url"
              value={form.url}
              onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
              placeholder="https://mi-sistema.cl/beartrack"
              required
            />
            <p className="text-xs text-fg-muted">
              {t("Tiene que ser https y responder en menos de 10 segundos.")}
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setCreando(false)}>
              {t("Cancelar")}
            </Button>
            <Button type="submit" disabled={guardando}>
              {guardando ? t("Creando…") : t("Crear webhook")}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
